import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Sun, Moon, Menu, X, Wrench, Phone, ChevronDown, LogOut, Calendar, ClipboardList } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import './Navbar.css';

const navLinks = [
  { to: '/',         label: 'Home' },
  { to: '/services', label: 'Services' },
  { to: '/booking',  label: 'Book Now' },
  { to: '/records',  label: 'My Records' },
  { to: '/about',    label: 'About Us' },
];

export default function Navbar() {
  const { theme, toggleTheme }                  = useTheme();
  const { user, logout, openLogin, openSignup } = useAuth();
  const [scrolled, setScrolled]                 = useState(false);
  const [menuOpen, setMenuOpen]                 = useState(false);
  const [dropdownOpen, setDropdownOpen]         = useState(false);
  const location                                = useLocation();
  const dropdownRef                             = useRef();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); setDropdownOpen(false); }, [location]);

  useEffect(() => {
    const handler = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => { logout(); setDropdownOpen(false); };
  const firstName = user?.name?.split(' ')[0];

  return (
    <header className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}>
      <div className="container navbar__inner">
        <NavLink to="/" className="navbar__logo">
          <div className="navbar__logo-icon">
            <img src="/images/logo-icon.png" alt="" className="navbar__logo-img"
              onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
            <span className="navbar__logo-icon-fallback"><Wrench size={18} /></span>
          </div>
          <div className="navbar__logo-text">
            <span className="navbar__logo-name">AutoElite</span>
            <span className="navbar__logo-tagline">Premium Services</span>
          </div>
        </NavLink>

        <nav className="navbar__links">
          {navLinks.map(({ to, label }) => (
            <NavLink key={to} to={to} end={to === '/'}
              className={({ isActive }) => `navbar__link ${isActive ? 'navbar__link--active' : ''}`}>
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="navbar__actions">
          <a href="tel:+917719412031" className="navbar__phone">
            <Phone size={13} /><span>+91 77194 12031</span>
          </a>
          <button className="navbar__theme-btn" onClick={toggleTheme} aria-label="Toggle theme">
            {theme === 'light' ? <Moon size={17} /> : <Sun size={17} />}
          </button>

          {!user && (
            <div className="navbar__auth-btns">
              <button className="btn btn-ghost navbar__login-btn" onClick={openLogin}>Sign In</button>
              <button className="btn btn-primary navbar__cta" onClick={openSignup}>Sign Up</button>
            </div>
          )}

          {user && (
            <div className="navbar__user" ref={dropdownRef}>
              <button className="navbar__user-btn" onClick={() => setDropdownOpen(o => !o)}>
                <div className="navbar__user-avatar">
                  {user.name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}
                </div>
                <span className="navbar__user-name">Hi, {firstName}</span>
                <ChevronDown size={13} className={`navbar__chevron ${dropdownOpen ? 'open' : ''}`} />
              </button>
              {dropdownOpen && (
                <div className="navbar__dropdown">
                  <div className="navbar__dropdown-header">
                    <p className="navbar__dropdown-name">{user.name}</p>
                    <p className="navbar__dropdown-email">{user.email}</p>
                    {user.vehicle && <p className="navbar__dropdown-vehicle">{user.vehicle}</p>}
                  </div>
                  <div className="navbar__dropdown-divider" />
                  <NavLink to="/booking" className="navbar__dropdown-item"><Calendar size={14} /> Book a Service</NavLink>
                  <NavLink to="/records" className="navbar__dropdown-item"><ClipboardList size={14} /> My Service Records</NavLink>
                  <div className="navbar__dropdown-divider" />
                  <button className="navbar__dropdown-item navbar__dropdown-item--danger" onClick={handleLogout}>
                    <LogOut size={14} /> Sign Out
                  </button>
                </div>
              )}
            </div>
          )}

          <button className="navbar__hamburger" onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <div className={`navbar__mobile ${menuOpen ? 'navbar__mobile--open' : ''}`}>
        <div className="navbar__mobile-inner">
          {navLinks.map(({ to, label }) => (
            <NavLink key={to} to={to} end={to === '/'}
              className={({ isActive }) => `navbar__mobile-link ${isActive ? 'navbar__mobile-link--active' : ''}`}>
              {label}
            </NavLink>
          ))}
          <div className="navbar__mobile-footer">
            {!user ? (
              <div style={{ display:'flex', gap:10 }}>
                <button className="btn btn-outline" style={{ flex:1, justifyContent:'center' }} onClick={() => { setMenuOpen(false); openLogin(); }}>Sign In</button>
                <button className="btn btn-primary" style={{ flex:1, justifyContent:'center' }} onClick={() => { setMenuOpen(false); openSignup(); }}>Sign Up</button>
              </div>
            ) : (
              <div className="navbar__mobile-user">
                <div className="navbar__user-avatar">{user.name.split(' ').map(n=>n[0]).join('').slice(0,2)}</div>
                <div style={{ flex:1 }}>
                  <p style={{ fontWeight:600, fontSize:14 }}>{user.name}</p>
                  <p style={{ fontSize:12, color:'var(--text-muted)' }}>{user.email}</p>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={handleLogout}><LogOut size={14} /> Sign Out</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}